/** 商家 AI 官网工作台：按 siteId 拼路径、解析旧 URL、选主站。 */

export const BRAND_SITE_SECTIONS = ["overview", "edit", "pages", "publish", "domain", "indexing"] as const;

export type BrandSiteSection = (typeof BRAND_SITE_SECTIONS)[number];

export type BrandSiteListItem = {
  id: number;
  is_primary?: boolean;
};

export function isBrandSiteSection(value: string | null | undefined): value is BrandSiteSection {
  return BRAND_SITE_SECTIONS.includes(value as BrandSiteSection);
}

export function brandSitePath(
  accountId: string | number,
  brandId: string | number,
  siteId: string | number,
  section: BrandSiteSection = "overview",
): string {
  return `/accounts/${accountId}/brands/${brandId}/apps/brand_site/sites/${siteId}/${section}`;
}

/** 品牌容器内的 AI 官网列表（尚未进入某一座，不滑入产品面板） */
export function brandSiteListPath(accountId: string | number, brandId: string | number): string {
  return `/accounts/${accountId}/brands/${brandId}/brand_site`;
}

/** 运营工具入口：AI 官网留在品牌容器，其它工具进产品面板 */
export function brandOperatingToolPath(
  accountId: string | number,
  brandId: string | number,
  appKey: string,
  section = "overview",
): string {
  if (appKey === "brand_site") return brandSiteListPath(accountId, brandId);
  return `/accounts/${accountId}/brands/${brandId}/apps/${appKey}/${section}`;
}

/** 产品面板「返回」：AI 官网回列表，其它工具回品牌页 */
export function productPanelBackPath(
  accountId: string | number,
  brandId: string | number,
  appKey: string | null | undefined,
): string {
  if (appKey === "brand_site") return brandSiteListPath(accountId, brandId);
  return `/accounts/${accountId}/brands/${brandId}`;
}

export function productPanelBackLabel(appKey: string | null | undefined): string {
  return appKey === "brand_site" ? "返回官网列表" : "返回品牌";
}

/** app 仓自定义域 API（带当前站，避免写到主站） */
export function brandSiteDomainApiPath(
  accountId: string | number,
  brandId: string | number,
  siteId: string | number,
  action?: "probe",
): string {
  const suffix = action === "probe" ? "/probe" : "";
  return `/accounts/${accountId}/brands/${brandId}/brand_site_domain${suffix}?brand_site_id=${encodeURIComponent(String(siteId))}`;
}

/** 无 siteId 的旧路径末段 → 合法 section，否则 overview */
export function brandSiteSectionFromPathname(pathname: string): BrandSiteSection {
  const scoped = pathname.match(
    /\/apps\/brand_site(?:\/sites\/[^/]+)?(?:\/([^/?#]+))?/,
  );
  const raw = scoped?.[1];
  return isBrandSiteSection(raw) ? raw : "overview";
}

export function parseBrandSiteWorkbenchPath(pathname: string): {
  accountId: string;
  brandId: string;
  siteId: string;
  section: BrandSiteSection;
} | null {
  const match = pathname.match(
    /^\/accounts\/([^/]+)\/brands\/([^/]+)\/apps\/brand_site\/sites\/([^/]+)(?:\/([^/?#]+))?/,
  );
  if (!match) return null;
  return {
    accountId: match[1],
    brandId: match[2],
    siteId: match[3],
    section: isBrandSiteSection(match[4]) ? match[4] : "overview",
  };
}

/** is_primary 优先，否则列表首条 */
export function primaryBrandSiteId(sites: BrandSiteListItem[]): number | null {
  const primary = sites.find((site) => site.is_primary);
  return (primary ?? sites[0])?.id ?? null;
}

export function brandSiteInList(sites: BrandSiteListItem[], siteId: string | number): boolean {
  const id = Number(siteId);
  if (!Number.isFinite(id) || id <= 0) return false;
  return sites.some((site) => site.id === id);
}
